import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, ShieldCheck } from "lucide-react";
import { GRIEVANCE_FORM_URL } from "../components/LegalLinks";
import logo from "../assets/twu-logo.png";

function Section({ title, children }) {
  return (
    <section className="mb-8" data-testid={`policy-section-${title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
      <h2 className="font-head text-lg font-extrabold tracking-tight mb-2">{title}</h2>
      <div className="text-sm leading-relaxed text-muted-foreground space-y-2">{children}</div>
    </section>
  );
}

export default function SmsPrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center gap-4">
          <img src={logo} alt="Transport Workers Union Local 555" className="w-16 h-auto" />
          <div>
            <div className="font-head font-extrabold text-sm leading-none tracking-tight">TWU LOCAL 555 BNA</div>
            <div className="text-[10px] tracking-[0.3em] text-muted-foreground mt-1">GRIEVANCE TRACKER</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="sms-privacy-policy">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> SMS Privacy Policy
        </p>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">
          TWU Local 555 BNA Grievance Tracker SMS Privacy Policy
        </h1>
        <p className="text-sm text-muted-foreground mt-2" data-testid="policy-effective-date">Effective Date: July 26, 2026</p>

        <div className="h-px bg-border my-8" />

        <div className="mb-8 border-2 border-primary rounded-sm p-4 bg-secondary/40" data-testid="policy-voluntary-callout">
          <p className="text-sm leading-relaxed text-foreground">
            <strong>Opting in to SMS is entirely voluntary.</strong> Consent to receive text messages is never required
            to file a grievance, receive union representation, maintain membership, create an account, or use the TWU
            Local 555 BNA Grievance Tracker. SMS consent is requested separately from any other agreement and can be
            withdrawn at any time by replying STOP.
          </p>
        </div>

        <Section title="Privacy Policy">
          <p>
            TWU Local 555 BNA Grievance Tracker ("we," "our," or "us") respects your privacy and is committed to
            protecting the personal information you provide when enrolling in our SMS messaging program.
          </p>
        </Section>

        <Section title="How We Obtain Your Consent (Opt-In)">
          <p>
            We collect your mobile number and messaging consent only through an affirmative, voluntary opt-in that you
            initiate. Consent is obtained by one or both of the following methods:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              You opt in on the official{" "}
              <a href={GRIEVANCE_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="policy-grievance-form-link"
                className="text-foreground underline underline-offset-2 hover:no-underline">
                TWU Local 555 Grievance Form (PDF)
              </a>, which includes a separate, unchecked-by-default SMS consent checkbox you must actively check.
              Filing the grievance does not require checking it; or
            </li>
            <li>You provide your number and expressly request grievance-related text updates from your TWU Local 555 BNA Grievance Representative (verbally or in writing).</li>
          </ul>
          <p>
            Your consent applies only to grievance-related communications for your specific case. You can complete any
            primary action (such as filing a grievance) without opting in to SMS, and opting out never affects your
            grievance or representation. See our{" "}
            <Link to="/sms-terms" className="text-foreground underline underline-offset-2 hover:no-underline">SMS Terms &amp; Conditions</Link>{" "}
            for the full program terms.
          </p>
        </Section>

        <Section title="Information We Collect">
          <p>When you opt in to receive SMS messages, we may collect:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Your mobile phone number</li>
            <li>Your name (if provided)</li>
            <li>Information necessary to identify your grievance case</li>
            <li>Your messaging preferences</li>
          </ul>
          <p>This information is used solely to provide grievance-related updates and communications.</p>
        </Section>

        <Section title="How We Use Your Information">
          <p>We use your mobile number to send:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Updates regarding the status of your grievance</li>
            <li>Requests for information or documentation related to your grievance</li>
            <li>Notifications about meetings, hearings, or deadlines</li>
            <li>Other grievance-related communications</li>
          </ul>
        </Section>

        <Section title="Mobile Number Privacy">
          <p>
            Your mobile phone number and SMS opt-in information will never be sold, rented, shared, or disclosed to
            third parties or affiliates for marketing or promotional purposes. Your mobile number is used exclusively
            for communications related to your grievance and the administration of the TWU Local 555 BNA Grievance
            Tracker.
          </p>
          <p>
            No mobile information or SMS opt-in data will be shared with any third parties or affiliates for any
            purpose. Text messaging originator opt-in data and consent will not be shared with any third parties,
            except with service providers strictly necessary to deliver our text messages (for example, our SMS
            provider), who are prohibited from using it for any other purpose.
          </p>
        </Section>

        <Section title="Message Frequency">
          <p>
            Message frequency varies depending on the status and activity of your grievance. Most participants will
            receive messages only when there is an update or action required.
          </p>
        </Section>

        <Section title="Message and Data Rates">
          <p>
            Message and data rates may apply according to your mobile carrier's plan. Please contact your wireless
            provider for details regarding any applicable charges.
          </p>
        </Section>

        <Section title="Opting Out">
          <p>
            You may opt out of SMS communications at any time by replying <strong className="text-foreground">STOP</strong> to
            any message. After opting out, you will no longer receive SMS updates unless you choose to opt in again.
          </p>
        </Section>

        <Section title="Help">
          <p>
            For assistance with the SMS program, reply <strong className="text-foreground">HELP</strong> to any message or
            contact your TWU Local 555 BNA Grievance Representative.
          </p>
        </Section>

        <Section title="Data Security">
          <p>
            We take reasonable administrative and technical measures to protect the information we collect from
            unauthorized access, disclosure, alteration, or destruction.
          </p>
        </Section>

        <Section title="Changes to This Privacy Policy">
          <p>
            We may update this Privacy Policy from time to time. Any changes will be posted with an updated Effective
            Date.
          </p>
        </Section>

        <Section title="Contact Us">
          <p>
            If you have questions regarding this Privacy Policy or the SMS messaging program, please contact:
          </p>
          <div className="mt-2 border border-border bg-card rounded-sm p-4 text-foreground">
            <div className="font-medium">TWU Local 555 BNA Grievance Tracker</div>
            <a href="mailto:bna.ramp@twu555.org" data-testid="policy-contact-email"
              className="mt-2 flex items-center gap-2 text-sm hover:underline underline-offset-2">
              <Mail className="w-4 h-4" /> bna.ramp@twu555.org
            </a>
            <a href="tel:+13049935996" data-testid="policy-contact-phone"
              className="mt-1 flex items-center gap-2 text-sm hover:underline underline-offset-2">
              <Phone className="w-4 h-4" /> 304-993-5996
            </a>
          </div>
        </Section>

        <div className="h-px bg-border my-8" />
        <Link to="/" data-testid="policy-back-home"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Grievance Tracker
        </Link>
      </main>
    </div>
  );
}
